import React from "react";
import { Box, Typography, Container, Slider } from "@material-ui/core";
import LOGO from "src/assets/svgs/logo.png";
import { Formik } from "formik";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import { QualifyingQuestions } from "src/interfaces/onboarding.interface";
import { updateUserInitialQuestionnaire } from "src/apiService/modules/users";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import { useSelector } from "react-redux";
import { userQualifyingQuestionsSelector } from "src/store/system/selector";
import { Redirect } from "react-router";
import type { Theme } from "src/theme";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import * as Yup from "yup";

const useStyles = makeStyles((theme: Theme) => ({
  sliderLight: {
    "& .MuiSlider-valueLabel > span > span": {
      color: "white",
    },
  },
  sliderDark: {
    "& .MuiSlider-valueLabel > span > span": {
      color: "black",
    },
  },
  submitButton: {
    backgroundColor: theme.palette.colors.green,
    color: "white",
    width: "50%",
    "&:hover": {
      backgroundColor: theme.palette.colors.lightGreen,
    },
  },
  confidenceSlider: {
    marginTop: theme.spacing(5),
  },
  errorField: {
    color: "red",
  },
  formControlArea: {
    marginBottom: theme.spacing(3),
  },
  formHeader: {
    marginBottom: theme.spacing(2),
    color: theme.palette.custom.derived.titleOnBg,
    fontWeight: theme.typography.fontWeightMedium,
  },
  error: {
    color: "red",
  },
  logoContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: theme.spacing(18),
  },
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    height: theme.spacing(18),
  },
  submitContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(5),
  },
}));

const marks = [
  {
    value: 1,
    label: "1",
  },
  {
    value: 2,
    label: "2",
  },
  {
    value: 3,
    label: "3",
  },
  {
    value: 4,
    label: "4",
  },
  {
    value: 5,
    label: "5",
  },
  {
    value: 6,
    label: "6",
  },
  {
    value: 7,
    label: "7",
  },
  {
    value: 8,
    label: "8",
  },
  {
    value: 9,
    label: "9",
  },
  {
    value: 10,
    label: "10",
  },
];

const getSliderColor = (value: number) => {
  switch (value) {
    case 1:
      return "#FF0000";
    case 2:
      return "#FF3300";
    case 3:
      return "#FF6600";
    case 4:
      return "#FF8000";
    case 5:
      return "#FFB300";
    case 6:
      return "#FFD000";
    case 7:
      return "#FFFF00";
    case 8:
      return "#CCFF00";
    case 9:
      return "#66FF00";
    case 10:
      return "#00FF00";
  }
};

const schema = Yup.object().shape({
  confidence: Yup.number().required(),
  current_system: Yup.string().required("Please provide a response."),
  avg_annual_sales: Yup.string().required("Please provide a response."),
});

const QualifyingQuestionsPage: React.FC = () => {
  const recordEvent = useRecordEvent();
  const qualifyingQuestions = useSelector(userQualifyingQuestionsSelector);
  const [values] = React.useState({
    confidence: 1,
    current_system: "",
    avg_annual_sales: "",
  });
  const classes = useStyles();
  const [currentTextClass, setCurrentTextClass] = React.useState("sliderLight");

  if (qualifyingQuestions) {
    return <Redirect to="/dashboard" />;
  }

  function valuetext(value: number) {
    return `${value}`;
  }

  return (
    <Container maxWidth="sm">
      <Box className={classes.logoContainer}>
        <img src={LOGO} alt="My Reseller Genie" height="100%" />
      </Box>
      <Box className={classes.headerContainer}>
        <Typography variant="h5" component="h1" className={classes.formHeader}>
          Welcome to My Reseller Genie!
        </Typography>
        <Typography variant="body2" className="text-center">
          We're excited for you to get going. We have a couple questions to help
          us understand your business needs:
        </Typography>
      </Box>
      <Box>
        <Formik
          initialValues={values}
          onSubmit={async (values: QualifyingQuestions) => {
            await updateUserInitialQuestionnaire(values);
            recordEvent({
              type: "feature-adoption",
              feature: "qualifying-questions-completed",
              confidence: values.confidence,
              currentSystem: values.current_system,
              avgAnnualSales: values.avg_annual_sales,
            });
          }}
          validationSchema={schema}
        >
          {({
            values,
            handleChange,
            handleSubmit,
            setFieldValue,
            isSubmitting,
            errors,
            touched,
          }) => (
            <form onSubmit={handleSubmit}>
              <Box display="flex" flexDirection="column">
                <Box display="flex" flexDirection="column">
                  <FormControl fullWidth className={classes.formControlArea}>
                    <Typography gutterBottom>
                      1. What is your confidence in your reseller bookkeeping
                      knowledge? (1 = lowest, 10 = very confident)
                    </Typography>
                    <Slider
                      classes={{ root: classes[currentTextClass] }}
                      className={classes.confidenceSlider}
                      defaultValue={values.confidence}
                      getAriaValueText={valuetext}
                      step={1}
                      marks={marks}
                      valueLabelDisplay="on"
                      max={10}
                      min={1}
                      onChange={(_, value) => {
                        setFieldValue("confidence", value);
                        const v = Array.isArray(value) ? value[0] : value;
                        if (v > 4) {
                          setCurrentTextClass("sliderDark");
                        } else {
                          setCurrentTextClass("sliderLight");
                        }
                      }}
                      style={{ color: getSliderColor(values.confidence) }}
                      value={values.confidence}
                    />
                  </FormControl>
                  <FormControl fullWidth className={classes.formControlArea}>
                    <Typography gutterBottom>
                      2. What are you currently using for bookkeeping?
                    </Typography>
                    <Select
                      value={values.current_system}
                      onChange={handleChange}
                      name="current_system"
                    >
                      <MenuItem value={"your-own-spreadsheet"}>
                        Your own spreadsheet
                      </MenuItem>
                      <MenuItem value={"someone-else-spreadsheet"}>
                        Someone else's spreadsheet
                      </MenuItem>
                      <MenuItem value={"quickbooks"}>Quickbooks</MenuItem>
                      <MenuItem value={"seller-ledger"}>Seller Ledger</MenuItem>
                      <MenuItem value={"flipwise"}>Flipwise</MenuItem>
                      <MenuItem value={"nothing"}>Nothing</MenuItem>
                      <MenuItem value={"other"}>Other</MenuItem>
                    </Select>
                    {errors.current_system && touched.current_system ? (
                      <div className={classes.errorField}>
                        {errors.current_system}
                      </div>
                    ) : null}
                  </FormControl>
                  <FormControl fullWidth>
                    <Typography gutterBottom>
                      3. What are your average annual sales?
                    </Typography>
                    <Select
                      value={values.avg_annual_sales}
                      onChange={handleChange}
                      name="avg_annual_sales"
                    >
                      <MenuItem value={"$0-$12,000"}>$0-$12,000</MenuItem>
                      <MenuItem value={"$12,001-$36,000"}>
                        $12,001-$36,000
                      </MenuItem>
                      <MenuItem value={"$36,001-$120,000"}>
                        $36,001-$120,000
                      </MenuItem>
                      <MenuItem value={"$120,001+"}>$120,001+</MenuItem>
                    </Select>
                    {errors.avg_annual_sales && touched.avg_annual_sales ? (
                      <div className={classes.error}>
                        {errors.avg_annual_sales}
                      </div>
                    ) : null}
                  </FormControl>
                </Box>
              </Box>
              <Box className={classes.submitContainer}>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className={classes.submitButton}
                >
                  Submit
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </Container>
  );
};

export default QualifyingQuestionsPage;
